import { CopyId } from "@/components/copy-id";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import {
  type Club,
  type RecommendedClubsParams,
  clubsProvider,
} from "@/providers/clubs.provider";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSparkles,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const DEFAULT_COUNTRY = "BRA";
const DAY_MS = 24 * 60 * 60 * 1000;

// Raw score multipliers, not a normalized mix — each one is independent.
const DEFAULT_WEIGHTS = { w_member_count: 1, w_recency: 0 };

type WeightKey = keyof typeof DEFAULT_WEIGHTS;

const WEIGHT_FIELDS: {
  key: WeightKey;
  label: string;
  desc: string;
  max: number;
  step: number;
}[] = [
  {
    key: "w_member_count",
    label: "Member count",
    desc: "Multiplied by the club's member count. With w_recency at 0 this is plain member-count ordering.",
    max: 10,
    step: 0.1,
  },
  {
    key: "w_recency",
    label: "Recency",
    desc: "Multiplied by e^(-age / 30 days): a brand-new club contributes the full weight, decaying toward 0 over about a month.",
    max: 100,
    step: 1,
  },
];

function estimateScore(club: Club, w: typeof DEFAULT_WEIGHTS) {
  const ageMs = Date.now() - new Date(club.created_at).getTime();
  const decay = Math.exp(-Math.max(ageMs, 0) / (30 * DAY_MS));
  return w.w_member_count * club.member_count + w.w_recency * decay;
}

function WeightSlider({
  label,
  name,
  value,
  max,
  step,
  onChange,
}: {
  label: string;
  name: string;
  value: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  function commit(raw: string) {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) onChange(parsed);
    setDraft(null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <input
          type="text"
          inputMode="decimal"
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && commit((e.target as HTMLInputElement).value)
          }
          className="h-9 w-20 sm:h-7 font-mono text-xs tabular-nums text-right bg-muted rounded px-1.5 border border-border focus:outline-none focus:border-primary"
        />
      </div>
      <input
        type="range"
        name={name}
        min={0}
        max={max}
        step={step}
        value={Math.min(value, max)}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-primary"
      />
    </div>
  );
}

export function ClubRecommendationsTab() {
  const [weightsOn, setWeightsOn] = useState(false);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [committed, setCommitted] = useState<RecommendedClubsParams>({
    page: 1,
    country_code: DEFAULT_COUNTRY,
  });

  const page = committed.page ?? 1;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: queryKeys.clubsRecommended(committed),
    queryFn: async () => {
      const t0 = performance.now();
      const result = await clubsProvider.getRecommended(committed);
      return { result, ms: Math.round(performance.now() - t0) };
    },
  });

  const clubs = data?.result.data;
  const hasNext = data?.result.has_next ?? false;
  const overridden =
    committed.w_member_count !== undefined ||
    committed.w_recency !== undefined;
  const estimateWeights = {
    w_member_count: committed.w_member_count ?? DEFAULT_WEIGHTS.w_member_count,
    w_recency: committed.w_recency ?? DEFAULT_WEIGHTS.w_recency,
  };

  function handleFetch() {
    const code = country.trim().toUpperCase();
    const next: RecommendedClubsParams = {
      page: 1,
      ...(code ? { country_code: code } : {}),
      ...(weightsOn ? weights : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(committed)) refetch();
    else setCommitted(next);
  }

  function goToPage(p: number) {
    setCommitted((c) => ({ ...c, page: p }));
  }

  function scoreCell(club: Club) {
    const score =
      club.score ?? (overridden ? estimateScore(club, estimateWeights) : null);
    if (score === null) return "—";
    return score.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Controls */}
      <div className="rounded-lg border p-4 flex flex-col gap-5">
        {/* Country */}
        <div className="flex flex-col gap-3">
          <Label htmlFor="club-rec-country" className="text-sm font-medium">
            Country
          </Label>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              id="club-rec-country"
              placeholder="ISO code (e.g. BRA)"
              maxLength={3}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full sm:w-44 font-mono text-sm uppercase"
            />
            <button
              type="button"
              onClick={() => setCountry("")}
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
            >
              All countries
            </button>
            <button
              type="button"
              onClick={() => setCountry(DEFAULT_COUNTRY)}
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
            >
              Reset to {DEFAULT_COUNTRY}
            </button>
          </div>
        </div>

        {/* Weights */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="club-weights-toggle" className="text-sm font-medium">
              Override weights
            </Label>
            <Switch
              id="club-weights-toggle"
              checked={weightsOn}
              onCheckedChange={setWeightsOn}
            />
            {weightsOn && (
              <button
                type="button"
                onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Reset to defaults
              </button>
            )}
          </div>
          {weightsOn && (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-muted-foreground">
                score = member_count × members + recency × e^(-age / 30d).
                Per-request values win over the published recommendations
                config.
              </p>
              <div className="grid grid-cols-1 gap-y-5">
                {WEIGHT_FIELDS.map(({ key, label, desc, max, step }) => (
                  <div key={key} className="flex flex-col gap-1">
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    <WeightSlider
                      label={`${label} (${key})`}
                      name={key}
                      value={weights[key]}
                      max={max}
                      step={step}
                      onChange={(v) => setWeights((w) => ({ ...w, [key]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleFetch}
            disabled={isFetching}
            className="gap-1.5 h-9 w-full sm:w-auto"
          >
            <IconSparkles className="size-4" />
            Fetch recommendations
          </Button>
          {data && !isFetching && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {data.ms} ms
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Club ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-right">Members</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load recommendations.{" "}
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="underline hover:text-foreground"
                  >
                    Retry
                  </button>
                </TableCell>
              </TableRow>
            ) : !clubs?.length ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  No recommendations returned.
                </TableCell>
              </TableRow>
            ) : (
              clubs.map((club, i) => {
                const pageSize = data?.result.page_size ?? clubs.length;
                return (
                  <TableRow key={club.club_id}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {(page - 1) * pageSize + i + 1}
                    </TableCell>
                    <TableCell>
                      <CopyId id={club.club_id} />
                    </TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {club.club_name}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {club.country_code ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(club.created_at)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">
                      {scoreCell(club)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {club.member_count.toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {clubs && (page > 1 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => goToPage(page - 1)}
            className="gap-1 h-9 sm:h-8"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => goToPage(page + 1)}
            className="gap-1 h-9 sm:h-8"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
